/*
|--------------------------------------------------------------------------
| Component: Table.jsx
| Author: Sayed Ahmed
| Description: Reusable table component with loading state, empty fallback,
|              dynamic columns, optional index column, and actions.
|--------------------------------------------------------------------------
*/

import React from "react";
import SpinnerLoading from "../spinner-loading/SpinnerLoading";
import { useTranslation } from "react-i18next";

export default function Table({
  columns,               // [{ header: "Name", render: (row) => row.name }, ...]
  data,                  // Array of row objects
  loading,               // Boolean to show spinner
  emptyText = "",        // Fallback message if data is empty
  actions,               // Function: (row) => JSX (edit/delete buttons)
  showIndex = false,     // Whether to show row index
  startIndex = 0,        // Starting index if paginated
}) {
  const { t } = useTranslation();
  const resolvedEmptyText = emptyText || t("No data available");

  // Calculate total columns (base + optional index + actions)
  const totalCols = columns.length + (actions ? 1 : 0) + (showIndex ? 1 : 0);

  return (
    <table className="display w-100 dataTable">
      <thead>
        <tr>
          {showIndex && <th>#</th>}
          {columns.map((col, index) => (
            <th key={index}>{col.header}</th>
          ))}
          {actions && <th>{t("Actions")}</th>}
        </tr>
      </thead>
      <tbody>
        {loading ? (
          <tr>
            <td colSpan={totalCols}>
              <SpinnerLoading />
            </td>
          </tr>
        ) : data && data.length > 0 ? (
          data.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {showIndex && <td>{startIndex + rowIndex + 1}</td>}
              {columns.map((col, colIndex) => (
                <td key={colIndex}>{col.render(row)}</td>
              ))}
              {actions && <td>{actions(row)}</td>}
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={totalCols}>
              {resolvedEmptyText}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}
